import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  TextInput,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, fontSize, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import CompactActionButton from '../components/CompactActionButton';
import { useTranslation } from '../i18n/I18nContext';
import { api } from '../services/api';
import { formatMoney } from '../data/currencies';
import Avatar from '../components/Avatar';
import VerifiedBadge from '../components/VerifiedBadge';
import NotificationBellButton from '../components/NotificationBellButton';
import { floatingDockContentInset, floatingDockScrollInset } from '../components/floatingDockLayout';

type User = {
  /** 9.26 / D-641 — resmî hesap (mavi işaret). */
  is_official?: boolean;
  id: number;
  name: string;
  photo_url: string | null;
  university: string | null;
  profession: string | null;
  price_message: number;
  currency: string;
};

type Props = {
  /** 1.3.7 / D-665 — keşif isteği oturumla gider; engel süzgeci ancak öyle çalışır. */
  token?: string | null;
  onSelectUser: (userId: number) => void;
  onWallet: () => void; // artık kullanılmıyor, dock'a taşındı
  onNotificationCenter: () => void;
};

export default function DiscoverScreen({ token, onSelectUser, onNotificationCenter }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function fetchUsers(q?: string) {
    return api.discover(q, lang, token).then(setUsers);
  }

  function load() {
    setLoading(true);
    setFailed(false);
    fetchUsers(query.trim() || undefined)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    setLoading(true);
    setFailed(false);
    api.discover(undefined, lang, token)
      .then(setUsers)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [lang, token]);

  function onSearch(text: string) {
    setQuery(text);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchUsers(text.trim() || undefined).catch(() => {});
    }, 300);
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.black} />
      </View>
    );
  }

  if (failed) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>{t('discover.failed')}</Text>
        <CompactActionButton
              variant="primary"
              label={t('common.retry')}
              onPress={load}
              style={styles.retryBtn}
              testID="discover-retry"
            />
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.headerRow}>
        <NotificationBellButton onPress={onNotificationCenter} />
      </View>

      <TextInput
        style={styles.searchInput}
        placeholder={t('common.search')}
        placeholderTextColor={colors.gray400}
        value={query}
        onChangeText={onSearch}
        clearButtonMode="while-editing"
      />

      <FlatList
        data={users}
        keyExtractor={(item) => String(item.id)}
        contentContainerStyle={[styles.list, { paddingBottom: insets.bottom + floatingDockContentInset }]}
        scrollIndicatorInsets={{ bottom: insets.bottom + floatingDockScrollInset }}
        renderItem={({ item }) => (
          <TouchableOpacity style={styles.row} onPress={() => onSelectUser(item.id)}>
            <Avatar photoUrl={item.photo_url} name={item.name} size={52} style={styles.avatar} />
            <View style={styles.info}>
              <View style={styles.nameRow}>
                <View style={styles.nameWithBadge}>
                  <Text style={styles.name} numberOfLines={1}>{item.name}</Text>
                  {item.is_official && <VerifiedBadge size={15} />}
                </View>
                <Text style={styles.price} numberOfLines={1}>
                  {Number(item.price_message) === 0 ? t('discover.free') : formatMoney(item.price_message, item.currency, lang)}
                </Text>
              </View>
              {(item.profession || item.university) && (
                <Text style={styles.university} numberOfLines={1}>
                  {[item.profession, item.university].filter(Boolean).join(' · ')}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        )}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
        ListEmptyComponent={<Text style={styles.empty}>{t(query.trim() ? 'inbox.searchEmpty' : 'discover.empty')}</Text>}
      />
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.white,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  errorText: { fontSize: fontSize.md, color: colors.gray400, marginBottom: spacing.md },
  retryBtn: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.control,
    backgroundColor: colors.black,
  },
  retryText: { color: colors.white, fontSize: fontSize.md, fontWeight: '600' },
  walletButton: {
    fontSize: fontSize.md,
    color: colors.gray600,
  },
  searchInput: {
    marginHorizontal: spacing.lg,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.gray100,
    borderRadius: radius.control,
    fontSize: fontSize.md,
    color: colors.black,
  },
  empty: {
    fontSize: fontSize.md,
    color: colors.gray400,
    textAlign: 'center',
    paddingTop: spacing.xl,
  },
  list: {
    paddingHorizontal: spacing.lg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    marginRight: spacing.md,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // 9.26 / D-641 — ad uzunsa kısalan o, rozet değil. Fiyattan uzaklık grupta;
  // ad kendi içinde BÜYÜMÜYOR: `flex: 1` ilk sürümde adı satırın sonuna kadar
  // uzatıp işareti fiyatın dibine itmişti.
  nameWithBadge: {
    flexDirection: 'row', alignItems: 'center', flexShrink: 1, minWidth: 0, marginRight: spacing.sm,
  },
  name: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: fontSize.md,
    fontWeight: '600',
    color: colors.black,
  },
  price: {
    flexShrink: 0,
    fontSize: fontSize.sm,
    color: colors.gray400,
  },
  university: {
    fontSize: fontSize.sm,
    color: colors.gray400,
    marginTop: 2,
  },
  separator: {
    height: 1,
    backgroundColor: colors.gray100,
  },
});
